import { Box, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import FormTextField from '../../UI/FormTextField';
import MainButton from '../../UI/MainButton';
import { DeclaredStudentRow, emptyDeclaredStudent } from '../../../utils/declared-students';
import { formatPersonName } from '../../../utils/person-name-case';

type Props = {
	rows: DeclaredStudentRow[];
	onChange: (rows: DeclaredStudentRow[]) => void;
	validated?: boolean;
	studentsError?: string;
};

const DeclaredStudentsEditor = ({ rows, onChange, validated = false, studentsError = '' }: Props) => {
	const prevRowCount = useRef(rows.length);

	useEffect(() => {
		if (rows.length > prevRowCount.current) {
			const index = rows.length - 1;
			window.requestAnimationFrame(() => {
				document.getElementById(`student-first-${index}`)?.focus();
			});
		}
		prevRowCount.current = rows.length;
	}, [rows.length]);

	const updateRow = (index: number, field: 'firstName' | 'lastName', value: string) => {
		onChange(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
	};

	const blurRowField = (index: number, field: 'firstName' | 'lastName') => {
		const formatted = formatPersonName(rows[index][field]);
		if (formatted !== rows[index][field]) {
			updateRow(index, field, formatted);
		}
	};

	const addRow = () => {
		onChange([...rows, emptyDeclaredStudent()]);
	};

	const removeRow = (index: number) => {
		if (rows.length <= 1) return;
		onChange(rows.filter((_, i) => i !== index));
	};

	return (
		<Box
			display='flex'
			flexDirection='column'
			gap={1}
			width='100%'
		>
			{rows.map((row, index) => (
				<Box
					key={`declared-student-${index}`}
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
						gap: 1,
						alignItems: 'flex-start',
						width: '100%',
					}}
				>
					<Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
						<FormTextField
							id={`student-first-${index}`}
							label={`Imię ucznia ${index + 1}`}
							validated={validated}
							errorMsg={validated && row.firstName.trim() === '' && row.lastName.trim() !== '' ? 'Uzupełnij imię' : ''}
							value={row.firstName}
							valueChangeHandler={(v) => updateRow(index, 'firstName', v)}
							onBlur={() => blurRowField(index, 'firstName')}
						/>
					</Box>
					<Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
						<FormTextField
							id={`student-last-${index}`}
							label={`Nazwisko ucznia ${index + 1}`}
							validated={validated}
							errorMsg={
								validated && row.lastName.trim() === '' && row.firstName.trim() !== '' ? 'Uzupełnij nazwisko' : ''
							}
							value={row.lastName}
							valueChangeHandler={(v) => updateRow(index, 'lastName', v)}
							onBlur={() => blurRowField(index, 'lastName')}
						/>
					</Box>
					{rows.length > 1 && (
						<MainButton
							title='Usuń'
							onClick={() => removeRow(index)}
							sx={{ mt: { xs: 0, sm: 1 }, minWidth: 'auto', width: 'auto', p: '8px 16px' }}
						/>
					)}
				</Box>
			))}
			<MainButton
				title='Dodaj ucznia'
				onClick={addRow}
				sx={{ width: 'auto', p: '8px 24px' }}
			/>
			{validated && studentsError && (
				<Typography
					color='error.main'
					variant='body2'
				>
					{studentsError}
				</Typography>
			)}
		</Box>
	);
};

export default DeclaredStudentsEditor;
